import type { ReactNode } from 'react';
import { ShieldX } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/permissions/usePermissions';
import type { PermissionKey } from '@/lib/permissions/keys';

interface Props {
  /** Chave do catálogo. Tipada: chave inexistente falha na compilação. */
  k: PermissionKey;
  children: ReactNode;
}

/**
 * Guarda de rota por chave de permissão.
 *
 * ── Por que existe ──
 * Sete rotas do menu não têm guard nenhum (`/gestor`, `/metricas`, `/metas`,
 * `/comissionamento`, `/whatsapp-oficial`, `/whatsapp-saude`, `/vendedor`): a sidebar esconde o
 * item e a URL abre. E duas rotas divergem entre menu e guarda — `/contratos` aparece para
 * `manager` pelo `canAccessAnalytics` da sidebar mas era negada por `useIsAdmin` na rota, então o
 * gestor via o item e levava "Acesso negado" ao clicar. Lendo a MESMA chave nos dois lugares, a
 * divergência some por construção, não por disciplina.
 *
 * ── Nunca decidir carregando ──
 * `usePermissions` começa com o conjunto vazio. Negar durante o carregamento pisca "Acesso
 * negado" para quem tem acesso — é o erro que `useCanAccessAnalytics` já documentava. Aqui o
 * estado de carregamento tem render próprio.
 *
 * ── Este é o eixo da PESSOA ──
 * Para o eixo da ORGANIZAÇÃO (`goal_metrics_enabled`, `commissions_enabled`) use
 * `RequireOrgFlag`. Fundir os dois quebra em silêncio — o catálogo documenta essa separação, e
 * ela já foi violada uma vez neste repo.
 */
export function RequirePermission({ k, children }: Props) {
  const { has, loading } = usePermissions();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex min-h-64 items-center justify-center text-muted-foreground">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        <span className="ml-2">Verificando permissões…</span>
      </div>
    );
  }

  if (!has(k)) {
    return (
      <div className="container mx-auto px-6 py-8">
        <Alert variant="destructive">
          <ShieldX className="h-4 w-4" />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>
              Acesso negado. Esta página depende de uma permissão que os seus grupos não concedem.
            </span>
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
              Voltar ao Dashboard
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
